import {
  Box,
  Button,
  HStack,
  Heading,
  Image,
  LinkBox,
  LinkOverlay,
  Text,
  VStack,
  useBreakpointValue
} from "@chakra-ui/react"
import { flattenEdges } from "@pathwright/utils"
import { augmentationSectionFocusTarget } from "@pathwright/ux/src/components/augmentation/sectionFocus"
import { CarouselGallery } from "@pathwright/ux/src/components/gallery/Gallery"
import Link from "@pathwright/ux/src/components/link/Link"
import { CoverPageResourceFragment as CoverPageResource } from "@pathwright/graphql-client/src/generated"
import { useResourceDisplayPrice } from "../library/browse/utils"
import { getStoreResourceUrl } from "../utils/urls"
import CoverPageDivider from "./CoverPageDivider"
import { useCoverPageContext } from "./CoverPageProvider"
import { COVER_PAGE_PADDING } from "./constants"
import { COVER_PAGE_CLOSE_TARGET_STATE_KEY } from "./coverPageCloseTarget"
import CoverPageSectionTrigger from "./design/CoverPageSectionTrigger"
import { useCoverPageTranslate } from "./utils"

const getContributorLine = (resource: CoverPageResource) => {
  let contributorLine: string[] = []

  const total = resource?.contributors?.total || 0
  const contributors = flattenEdges(resource?.contributors) || []

  if (!contributors?.length) return ""

  contributors.slice(0, 2).forEach((a) => {
    contributorLine.push(a.full_name)
  })
  if (total > 2) {
    contributorLine.push(`+${total - 2}`)
  }
  return contributorLine.join(", ")
}

const MoreResource = ({ resource }: { resource: CoverPageResource }) => {
  // NOTE (TODO): the CoverPageResource resource does not have the license_offerings field expected by getResourcePriceRange.
  const displayPrice = useResourceDisplayPrice(resource)
  const closeTarget = useCoverPageContext((v) => v.closeTarget)
  const getResourceTo = (resource: CoverPageResource) => ({
    pathname: getStoreResourceUrl(resource.slug, true),
    state: {
      setResourceLaunchedFrom: true,
      ...(closeTarget
        ? { [COVER_PAGE_CLOSE_TARGET_STATE_KEY]: closeTarget }
        : {})
    }
  })

  const contributorLine = getContributorLine(resource)

  return (
    <LinkBox>
      <LinkOverlay asChild>
        <Link to={getResourceTo(resource)} />
      </LinkOverlay>

      <VStack
        borderRadius="card"
        shadow="card"
        bg="background"
        gap={0}
        p={0}
        h="100%"
        overflow="hidden"
        transition="all 0.2s"
        _hover={{
          shadow: "cardHover",
          "& img": {
            transform: "scale(1.03)"
          }
        }}
      >
        <Box w="100%" overflow="hidden" h="160px">
          <Image
            src={resource.image}
            alt={resource.name}
            borderTopRadius="card"
            maxW="100%"
            objectFit="cover"
            transition="transform 0.2s ease-in-out"
          />
        </Box>
        <VStack
          align="start"
          w="full"
          gap={0}
          p="10px 10px"
          minH="100px"
          bg="background"
        >
          <Heading
            as="h6"
            // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
            size="h6"
            my={0}
            className="theme-heading theme-heading-bold"
            lineClamp={contributorLine || displayPrice ? 2 : 3}
          >
            {resource.name}
          </Heading>
          {contributorLine && (
            <Text
              my={0}
              textAlign="left"
              fontSize="sm"
              color="blackAlpha.500"
              lineClamp={1}
            >
              {contributorLine}
            </Text>
          )}
          {displayPrice && (
            <Text
              my={0}
              textAlign="left"
              fontSize="sm"
              color="brand.600"
              lineClamp={1}
            >
              {displayPrice}
            </Text>
          )}
        </VStack>
      </VStack>
    </LinkBox>
  )
}

const CoverPageMore = () => {
  const { t } = useCoverPageTranslate()
  const categories = useCoverPageContext((v) => v.categories)
  const moreSectionRef = useCoverPageContext((v) => v.moreSectionRef)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)
  const moreResources = useCoverPageContext((v) => v.moreResources)
  const isDesignMode = useCoverPageContext((v) => v.isDesignMode)
  const isMobile = useCoverPageContext((v) => v.isMobile)
  const hasMoreResources = moreResources?.length > 0

  const itemsPerRow = useBreakpointValue({
    base: 1,
    sm: 2,
    md: 3
  })

  // No categories == no "More" resources; see useMoreResources.
  if (!categories.length || (!hasMoreResources && !isDesignMode)) return null

  return (
    <>
      <VStack
        id="more"
        w="100%"
        px={COVER_PAGE_PADDING}
        align="start"
        ref={moreSectionRef}
        scrollMarginTop="150px"
        overflow="hidden"
        position="relative"
        role="group"
      >
        <CoverPageSectionTrigger sectionKey="categories" top={"50px"} />
        {!immersiveActive && (
          <CoverPageDivider horizontalMargin={"0"} verticalMargin={"20px"} />
        )}
        <HStack
          justify="start"
          align="center"
          wrap="wrap"
          gap={4}
          // px={COVER_PAGE_PADDING}
          mb="20px"
        >
          {!immersiveActive && (
            <Heading
              as="h3"
              // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
              size="h7"
              mt={0}
              color="PRIMARY_GRAY"
              className="theme-heading theme-heading-semibold"
            >
              {t("related.more_in")}{" "}
            </Heading>
          )}
          {categories.map((category) => (
            <Button
              key={category.id}
              className="CoverPageCategoryPill"
              {...augmentationSectionFocusTarget()}
              asChild
              size="sm"
              variant="subtle"
              colorPalette="gray"
              borderRadius="card"
              px={4}
              fontSize="15px"
              fontWeight="500"
            >
              <Link
                to={`/library/?category=${category.name}`}
                variant="plain"
                textDecor="none"
              >
                {category.name}
              </Link>
            </Button>
          ))}
        </HStack>
        {hasMoreResources ? (
          <Box
            w="100%"
            css={{
              "& .carousel-container": {
                paddingBottom: "2rem"
              },
              "& .carousel-item": {
                maxWidth: "280px"
              }
            }}
          >
            <CarouselGallery
              items={moreResources}
              gap={0}
              spacingY="0"
              spacingX={
                isMobile ? COVER_PAGE_PADDING.base : COVER_PAGE_PADDING.md
              }
              enableStagger
              scrollBehavior={isMobile ? "smooth" : "snap"}
              itemsPerRow={itemsPerRow}
              itemsFillWidth={false}
              renderItem={(resource: CoverPageResource, { index }) => (
                <Box
                  key={resource.id}
                  py={2}
                  pl={index === 0 ? 1 : 3}
                  pr={index === moreResources.length - 1 ? 1 : 3}
                  h="100%"
                  css={{
                    "& .roundedTop.roundedBottom.roundedLeft.roundedRight": {
                      borderRadius: "var(--chakra-radii-card)",
                      overflow: "hidden"
                    }
                  }}
                >
                  <MoreResource resource={resource} />
                </Box>
              )}
            />
          </Box>
        ) : null}
      </VStack>
    </>
  )
}
export default CoverPageMore
